import { Box, useTheme } from "@mui/material";
import { forwardRef, Ref, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useRelationships } from "../../queries/object-relationships/UseObjectRelationships";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { ISearchResultsItem, ISearchResultsItemsGroup } from "../../services/object-search-data-service/Models";
import SearchViewItemList from "../../views/search-view/SearchViewItemList";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";

export type ObjectRelationshipsProps = {
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
}

export type ObjectRelationshipsType = {
    executeAction: (action: BaseActionEnum) => void;
}

function ObjectRelationships({
    setViewOptionState
}: ObjectRelationshipsProps, ref: Ref<unknown> | undefined) {
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const { t } = useTranslation();
    const theme = useTheme();

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        }
    });

    const objectName = objectViewState?.contextObject?.objectName ?? "";

    const { data, status, refetch } = useQueryNotifications(useRelationships)({
        groupID: routeContext.groupID as number,
        eLynxObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        }
    });

    useImperativeHandle<unknown, ObjectRelationshipsType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    )

    function executeAction(action: BaseActionEnum) {
        switch (action) {
            case BaseActionEnum.Refresh:
                refetch();
                break;
        }
    }

    const noDataMessage = (data?.groupsSection?.isVisible !== true &&
            data?.devicesSection?.isVisible !== true &&
            data?.tankBatteriesSection?.isVisible !== true &&
            data?.tanksSection?.isVisible !== true &&
            data?.wellsSection?.isVisible !== true)
        ? t('noData.noDataTypesForObject', { dataTypeName: t('dataTypeNames.relationship'), objectName })
        : undefined;

    return (
        <>
            <LoadingZone isLoading={false}>
                <Box sx={{
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1),
                    overflow: 'hidden',
                    overflowY: 'auto'
                }}>
                {status === "success" && noDataMessage &&
                            <NoData text={noDataMessage} />
                        }
                        {(data?.groupsSection?.items?.length ?? 0) > 0 &&
                            <SearchViewItemList header={t('search.groupsContaining')}
                                subHeader={t('search.membership')}
                                searchText={objectName}
                                items={[{
                                    label: undefined,
                                    items: data?.groupsSection?.items as ISearchResultsItem[]
                                }]}
                            />
                        }

                        {(data?.devicesSection.groups.length ?? 0) > 0 &&

                        <SearchViewItemList header={t('relationships.devices')}
                            subHeader={t('search.membership')}
                            searchText={objectName}
                            items={data?.devicesSection.groups as ISearchResultsItemsGroup[]} />
                        }

                        {(data?.wellsSection.groups.length ?? 0) > 0 &&
                            <SearchViewItemList header={t('search.wells')}
                                subHeader={t('search.membership')}
                                searchText={objectName}
                                items={data?.wellsSection.groups as ISearchResultsItemsGroup[]} />
                        }

                        {(data?.tankBatteriesSection.groups.length ?? 0) > 0 &&
                            <SearchViewItemList header={t('relationships.tankBatteries')}
                                subHeader={t('search.membership')}
                                searchText={objectName}
                                items={data?.tankBatteriesSection.groups as ISearchResultsItemsGroup[]} />
                        }

                        {(data?.tanksSection.groups.length ?? 0) > 0 &&
                            <SearchViewItemList header={t('relationships.tanks')}
                                subHeader={t('search.membership')}
                                searchText={objectName}
                                items={data?.tanksSection.groups as ISearchResultsItemsGroup[]} />
                        }
                </Box>
            </LoadingZone>
        </>
    )
}

export default forwardRef(ObjectRelationships);